import { z } from "zod";

import {
  cableConstructionSchema,
  conductorMaterialSchema,
  insulationMaterialSchema,
  jurisdictionProfileSchema,
} from "../services/cableContract";
import {
  allowanceBasisSchema,
  coverKindSchema,
  ratedLoadBasisSchema,
  trayArrangementSchema,
  trayTypeSchema,
} from "../services/cableTrayContract";
import {
  ALLOWANCE_BASIS_LABELS,
  ARRANGEMENT_LABELS,
  CABLE_TRAY_LABELS,
  COVER_LABELS,
  RATED_LOAD_BASIS_LABELS,
  TRAY_TYPE_LABELS,
} from "./cableTrayLabels";

// Draft model of the cable tray form (EOS-12, A34): what the user is typing, kept apart from the
// components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. Each row's cable comes from a stored EOS-06
// cable run of the active revision (LINKED, the default) or is typed (TYPED). A blank optional
// item (a source, the fill limit, the rated load, the support spacing, the allowance, a row's
// diameter or mass, a spacing) is left OUT of the payload: blank means NOT ESTABLISHED, and the
// engine must see it as absent, never as "". A required figure is sent as "" so the schema names
// it by its on-screen label; a choice the user has not made ("" = Choose...) is left out so the
// schema reports it as required.
//
// Rows are separate entries with stable ids, so adding or removing one never disturbs the
// others; the ids never reach the backend.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const CABLE_TRAY_DRAFT_MODULE = "cable-tray";

/** Where a row's cable comes from, on screen. */
export const CABLE_SOURCES = ["LINKED", "TYPED"] as const;
export type CableSourceChoice = (typeof CABLE_SOURCES)[number];

export type CableRowDraft = {
  id: string;
  code: string;
  name: string;
  cableSource: CableSourceChoice;
  cableRunId: string;
  construction: string;
  cores: string;
  conductorMaterial: string;
  insulationMaterial: string;
  cableSourceText: string;
  quantity: string;
  quantitySource: string;
  overallDiameterMm: string;
  diameterSource: string;
  massKgPerM: string;
  massSource: string;
  spacingMm: string;
  spacingInDiameters: string;
  spacingSource: string;
  sldElementCode: string;
};

export type CableTrayDraft = {
  code: string;
  name: string;
  trayType: string;
  arrangement: string;
  usableWidthMm: string;
  usableDepthMm: string;
  dimensionsSource: string;
  cover: string;
  fillLimitPercent: string;
  fillLimitSource: string;
  ratedLoadKg: string;
  ratedLoadBasis: string;
  ratedSpanM: string;
  ratedLoadSource: string;
  supportSpacingM: string;
  supportSpacingSource: string;
  futureAllowancePercent: string;
  futureAllowanceSource: string;
  futureAllowanceBasis: string;
  rows: CableRowDraft[];
  /** The feeders the route carries, comma-separated as typed. */
  feeders: string;
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const cableRowDraftSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    cableSource: z.enum(CABLE_SOURCES),
    cableRunId: z.string(),
    construction: chosenOrBlank(cableConstructionSchema),
    cores: z.string(),
    conductorMaterial: chosenOrBlank(conductorMaterialSchema),
    insulationMaterial: chosenOrBlank(insulationMaterialSchema),
    cableSourceText: z.string(),
    quantity: z.string(),
    quantitySource: z.string(),
    overallDiameterMm: z.string(),
    diameterSource: z.string(),
    massKgPerM: z.string(),
    massSource: z.string(),
    spacingMm: z.string(),
    spacingInDiameters: z.string(),
    spacingSource: z.string(),
    sldElementCode: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the fields
// above. A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const cableTrayDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    trayType: chosenOrBlank(trayTypeSchema),
    arrangement: chosenOrBlank(trayArrangementSchema),
    usableWidthMm: z.string(),
    usableDepthMm: z.string(),
    dimensionsSource: z.string(),
    cover: chosenOrBlank(coverKindSchema),
    fillLimitPercent: z.string(),
    fillLimitSource: z.string(),
    ratedLoadKg: z.string(),
    ratedLoadBasis: chosenOrBlank(ratedLoadBasisSchema),
    ratedSpanM: z.string(),
    ratedLoadSource: z.string(),
    supportSpacingM: z.string(),
    supportSpacingSource: z.string(),
    futureAllowancePercent: z.string(),
    futureAllowanceSource: z.string(),
    futureAllowanceBasis: chosenOrBlank(allowanceBasisSchema),
    rows: z.array(cableRowDraftSchema).min(1),
    feeders: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let nextRowNumber = 1;

// A prefix of its own, so a tray row id is never mistaken for another form's.
export function createCableRowId(): string {
  const id = `tray-row-${nextRowNumber}`;
  nextRowNumber += 1;
  return id;
}

// Row ids come from a counter that starts again at 1 on every page load, while a restored draft
// brings the ids it was saved with. Moving the counter past every id in use keeps a row added
// afterwards from taking the id of one already on screen.
export function seedCableRowIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^tray-row-(\d+)$/.exec(id);
    if (match !== null) {
      nextRowNumber = Math.max(nextRowNumber, Number(match[1]) + 1);
    }
  }
}

export function createCableRowDraft(): CableRowDraft {
  return {
    id: createCableRowId(),
    code: "",
    name: "",
    cableSource: "LINKED",
    cableRunId: "",
    construction: "",
    cores: "",
    conductorMaterial: "",
    insulationMaterial: "",
    cableSourceText: "",
    quantity: "",
    quantitySource: "",
    overallDiameterMm: "",
    diameterSource: "",
    massKgPerM: "",
    massSource: "",
    spacingMm: "",
    spacingInDiameters: "",
    spacingSource: "",
    sldElementCode: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile, no cover. Every choice
// starts blank; one empty row waits for the engineer, its cable on a linked cable run.
export function createInitialCableTrayDraft(): CableTrayDraft {
  return {
    code: "",
    name: "",
    trayType: "",
    arrangement: "",
    usableWidthMm: "",
    usableDepthMm: "",
    dimensionsSource: "",
    cover: "NONE",
    fillLimitPercent: "",
    fillLimitSource: "",
    ratedLoadKg: "",
    ratedLoadBasis: "PER_METRE",
    ratedSpanM: "",
    ratedLoadSource: "",
    supportSpacingM: "",
    supportSpacingSource: "",
    futureAllowancePercent: "",
    futureAllowanceSource: "",
    futureAllowanceBasis: "AREA_AND_LOAD",
    rows: [createCableRowDraft()],
    feeders: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// An optional whole number (a quantity, the cores): sent as a number, or left out when blank.
function optionalInteger(key: string, typed: string): Record<string, number | string> {
  const value = typed.trim();
  if (value === "") {
    return {};
  }
  const parsed = Number(value);
  return { [key]: Number.isInteger(parsed) ? parsed : value };
}

// A declared block with its source: sent when its figure is typed, left out when blank.
function declared(
  key: string,
  figureKey: string,
  figure: string,
  source: string,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  const value = figure.trim();
  if (value === "") {
    return {};
  }
  return { [key]: { [figureKey]: value, ...extra, ...optional("source", source) } };
}

// The cable as chosen on screen: the linked run's id alone, or the typed block alone. A blank
// run id is sent as "" so the schema names it; the builder never sends both.
function cablePayload(row: CableRowDraft): Record<string, unknown> {
  if (row.cableSource === "TYPED") {
    return {
      cable: {
        ...optional("construction", row.construction),
        ...optionalInteger("cores", row.cores),
        ...optional("conductor_material", row.conductorMaterial),
        ...optional("insulation_material", row.insulationMaterial),
        ...optional("source", row.cableSourceText),
      },
    };
  }
  return { cable_run_id: row.cableRunId.trim() };
}

// One row as the request carries it.
function rowPayload(row: CableRowDraft): Record<string, unknown> {
  return {
    code: row.code.trim(),
    name: row.name.trim(),
    ...cablePayload(row),
    ...optionalInteger("quantity", row.quantity),
    ...optional("quantity_source", row.quantitySource),
    ...optional("overall_diameter_mm", row.overallDiameterMm),
    ...optional("diameter_source", row.diameterSource),
    ...optional("mass_kg_per_m", row.massKgPerM),
    ...optional("mass_source", row.massSource),
    ...optional("spacing_mm", row.spacingMm),
    ...optional("spacing_in_diameters", row.spacingInDiameters),
    ...optional("spacing_source", row.spacingSource),
    ...optional("sld_element_code", row.sldElementCode),
  };
}

/** The feeders as typed, comma-separated, blanks dropped. */
export function feederCodes(typed: string): string[] {
  return typed
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
}

// The request body as the form will send it. It is validated afterwards with
// cableTrayRunCreateRequestSchema, so this function does not judge the values and never
// reorders the rows.
export function buildCableTrayRunPayload(
  draft: CableTrayDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  const feeders = feederCodes(draft.feeders);
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      ...optional("tray_type", draft.trayType),
      ...optional("arrangement", draft.arrangement),
      usable_width_mm: draft.usableWidthMm.trim(),
      usable_depth_mm: draft.usableDepthMm.trim(),
      ...optional("dimensions_source", draft.dimensionsSource),
      ...optional("cover", draft.cover),
      ...declared("fill_limit", "percent", draft.fillLimitPercent, draft.fillLimitSource),
      ...declared("rated_load", "value_kg", draft.ratedLoadKg, draft.ratedLoadSource, {
        ...optional("basis", draft.ratedLoadBasis),
        ...optional("rated_span_m", draft.ratedSpanM),
      }),
      ...declared("support_spacing", "value_m", draft.supportSpacingM, draft.supportSpacingSource),
      ...declared(
        "future_allowance",
        "percent",
        draft.futureAllowancePercent,
        draft.futureAllowanceSource,
        optional("basis", draft.futureAllowanceBasis),
      ),
      // Every row is sent, so the number in a message such as "Row 2 — Row code is required."
      // always matches the row's position on screen.
      rows: draft.rows.map(rowPayload),
      ...(feeders.length === 0 ? {} : { sld_element_codes: feeders }),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

export const CABLE_SOURCE_LABELS: Record<CableSourceChoice, string> = {
  LINKED: "Stored cable run of this revision",
  TYPED: "Typed cable",
};

export const CONDUCTOR_MATERIAL_LABELS = { COPPER: "Copper", ALUMINIUM: "Aluminium" } as const;

export const INSULATION_LABELS = {
  PVC: "PVC",
  XLPE: "XLPE",
  EPR: "EPR",
  MINERAL: "Mineral",
} as const;

export const CONSTRUCTION_LABELS = {
  SINGLE_CORE: "Single-core",
  MULTICORE: "Multicore",
} as const;

export {
  ALLOWANCE_BASIS_LABELS,
  ARRANGEMENT_LABELS,
  CABLE_TRAY_LABELS,
  COVER_LABELS,
  RATED_LOAD_BASIS_LABELS,
  TRAY_TYPE_LABELS,
};
